import { useState, useRef, useEffect } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

import { Upload, FileText, Loader2 } from "lucide-react";
import { PageUploadDialog, type InsertPagesData } from "./page-upload-dialog";

interface UploadSectionProps {
  onUploadComplete?: () => void;
  existingDocumentId?: string;
  onNavigateToVersion?: (documentId: string, versionNumber: number) => void;
  documentColor?: string;
  onPagesInserted?: () => void;
  onProcessingComplete?: (pageIds: string[]) => void;
}

export function UploadSection({ onUploadComplete, existingDocumentId, onNavigateToVersion, documentColor = "#ef4444", onPagesInserted, onProcessingComplete }: UploadSectionProps) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatusText, setUploadStatusText] = useState("");
  const [addPagesProgress, setAddPagesProgress] = useState(0);
  const [isAddPagesUploading, setIsAddPagesUploading] = useState(false);
  const [addPagesStatusText, setAddPagesStatusText] = useState("");
  const [uploadedPageData, setUploadedPageData] = useState<any>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadVersionDragActive, setUploadVersionDragActive] = useState(false);
  const [addPagesDragActive, setAddPagesDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addPagesFileInputRef = useRef<HTMLInputElement>(null);
  const uploadPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const trickleIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRealProgressRef = useRef(0);
  const lastProgressTimeRef = useRef(Date.now());
  const queryClient = useQueryClient();

  // Fetch current document data when existingDocumentId is present
  const { data: documentData } = useQuery<any>({
    queryKey: [`/api/documents/${existingDocumentId}`],
    enabled: !!existingDocumentId,
  });

  const clearTrickle = () => {
    if (trickleIntervalRef.current) {
      clearInterval(trickleIntervalRef.current);
      trickleIntervalRef.current = null;
    }
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (uploadPollIntervalRef.current) {
        clearInterval(uploadPollIntervalRef.current);
        uploadPollIntervalRef.current = null;
      }
      clearTrickle();
    };
  }, []);

  const uploadFileWithProgress = async (file: File, onXhrComplete?: () => void) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append("pdf", file);
      if (existingDocumentId) {
        formData.append("existingDocumentId", existingDocumentId);
      }

      let xhrCompleteFired = false;
      // Phase 1: Track upload progress (0-20%)
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const uploadPercent = Math.round((e.loaded / e.total) * 100);
          // Map upload to 0-20% of total progress
          const totalProgress = Math.round(uploadPercent * 0.2);
          setUploadProgress(totalProgress);
          setUploadStatusText("Uploading file to server...");
          // Fire once when bytes are fully sent — server is now processing
          if (uploadPercent >= 100 && !xhrCompleteFired) {
            xhrCompleteFired = true;
            onXhrComplete?.();
          }
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (error) {
            reject(new Error("Failed to parse response"));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("POST", "/api/documents/upload");
      xhr.send(formData);
    });
  };

  const uploadPagesFileWithProgress = async (file: File) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append("pdf", file);

      // Phase 1: Track upload progress (0-100%)
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setAddPagesProgress(percentComplete);
          setAddPagesStatusText("Uploading PDF...");
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            setAddPagesStatusText("Generating page previews...");
            resolve(data);
          } catch (error) {
            reject(new Error("Failed to parse response"));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("POST", `/api/documents/${existingDocumentId}/pages/upload`);
      xhr.send(formData);
    });
  };

  const handleUploadVersionDrag = (e: React.DragEvent, entering: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadVersionDragActive(entering);
  };

  const handleAddPagesDrag = (e: React.DragEvent, entering: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    setAddPagesDragActive(entering);
  };

  const handleUploadVersionDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadVersionDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type === "application/pdf") {
      handleFileUpload(file);
    }
  };

  const handleAddPagesDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAddPagesDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type === "application/pdf") {
      handleAddPagesFileUpload(file);
    }
  };

  const handleAddPagesFileUpload = async (file: File) => {
    if (file.type !== "application/pdf") {
      console.error("Invalid file type: Only PDF files are allowed");
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB
      console.error("File too large: Maximum file size is 50MB");
      return;
    }

    // Clear any stale data before starting new upload
    setUploadedPageData(null);

    try {
      setIsAddPagesUploading(true);
      setAddPagesProgress(0);
      
      // Upload file and track progress
      const data: any = await uploadPagesFileWithProgress(file);
      
      // Store the uploaded data
      setUploadedPageData(data);
      
      // Reset progress and open dialog for page selection
      setAddPagesProgress(0);
      setIsAddPagesUploading(false);
      setShowUploadDialog(true);
      
    } catch (error) {
      console.error("Add pages upload failed:", error);
      setIsAddPagesUploading(false);
      setAddPagesProgress(0);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (file.type !== "application/pdf") {
      console.error("Invalid file type: Only PDF files are allowed");
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB
      console.error("File too large: Maximum file size is 50MB");
      return;
    }

    // Clear any existing polling interval before starting a new upload
    if (uploadPollIntervalRef.current) {
      clearInterval(uploadPollIntervalRef.current);
      uploadPollIntervalRef.current = null;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      lastRealProgressRef.current = 0;
      lastProgressTimeRef.current = Date.now();
      setUploadStatusText("Uploading file to server...");
      
      // Phase 1: Upload file (0-20%)
      // When XHR bytes finish but server hasn't responded yet (cloud storage upload),
      // slowly trickle the bar from 20% to 26% so it doesn't look frozen.
      const data: any = await uploadFileWithProgress(file, () => {
        setUploadStatusText("Saving to secure storage...");
        clearTrickle();
        trickleIntervalRef.current = setInterval(() => {
          setUploadProgress(p => p < 26 ? p + 1 : p);
        }, 5000);
      });
      
      // Server responded — stop the pre-response trickle
      clearTrickle();
      
      // Track the current upload to prevent race conditions
      const currentVersionId = data.version.id;
      
      // Phase 2: Poll for server processing progress (20-100%)
      setUploadProgress(20);
      setUploadStatusText("Processing PDF...");
      
      const pollStartTime = Date.now();
      const pollTimeout = 120000; // 2 minutes timeout
      
      uploadPollIntervalRef.current = setInterval(async () => {
        // Guard against stale intervals from previous uploads
        if (currentVersionId !== data.version.id) {
          return;
        }
        
        try {
          if (Date.now() - pollStartTime > pollTimeout) {
            if (uploadPollIntervalRef.current) {
              clearInterval(uploadPollIntervalRef.current);
              uploadPollIntervalRef.current = null;
            }
            // Timeout - show warning and navigate anyway
            // Processing may still complete in background
            await queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
            if (data?.document?.id) {
              await queryClient.invalidateQueries({ queryKey: [`/api/documents/${data.document.id}`] });
            }
            setIsUploading(false);
            setUploadProgress(0);
            setUploadStatusText("");
            
            if (data && onNavigateToVersion) {
              onNavigateToVersion(data.document.id, data.version.version);
            }
            onUploadComplete?.();
            return;
          }

          const statusResponse = await fetch(`/api/documents/versions/${currentVersionId}/status`);
          
          // Check for network errors
          if (!statusResponse.ok) {
            console.error("Status poll returned non-OK status:", statusResponse.status);
            return; // Continue polling, timeout will handle permanent failures
          }
          
          const status = await statusResponse.json();
          
          // Intelligent progress mapping (20-100% range):
          // - Server 0-25%   → Frontend 20-39% (extraction phase - more granular)
          // - Server 25-100% → Frontend 40-100% (processing phase)
          let mappedProgress: number;
          if (status.progress <= 25) {
            mappedProgress = 20 + Math.round((status.progress / 25) * 19);
          } else {
            mappedProgress = 40 + Math.round(((status.progress - 25) / 75) * 60);
          }

          if (mappedProgress > lastRealProgressRef.current) {
            // Real progress moved — snap to it and reset the stall clock
            lastRealProgressRef.current = mappedProgress;
            lastProgressTimeRef.current = Date.now();
            setUploadProgress(Math.min(100, mappedProgress));
          } else if (Date.now() - lastProgressTimeRef.current > 4000) {
            // No real progress for 4s — trickle 1% at a time, capped 8% above last
            // real value and never above 95% (real completion will snap to 100%)
            setUploadProgress(prev => Math.min(prev + 1, lastRealProgressRef.current + 8, 95));
          }
          
          // Update status text — show page X of Y during conversion, fall back otherwise
          if (status.currentPage && status.pageCount && status.progress >= 25) {
            setUploadStatusText(`Converting page ${status.currentPage} of ${status.pageCount}...`);
          } else if (status.progress < 25) {
            setUploadStatusText("Extracting pages from PDF...");
          } else if (status.progress < 100) {
            setUploadStatusText("Processing...");
          }
          
          if (status.status === "completed") {
            if (uploadPollIntervalRef.current) {
              clearInterval(uploadPollIntervalRef.current);
              uploadPollIntervalRef.current = null;
            }
            setUploadProgress(100);
            setUploadStatusText("Complete!");
            
            // Invalidate queries
            await queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
            if (data?.document?.id) {
              await queryClient.invalidateQueries({ queryKey: [`/api/documents/${data.document.id}`] });
            }
            
            // Reset and navigate
            setTimeout(() => {
              setUploadProgress(0);
              setUploadStatusText("");
              setIsUploading(false);
              
              if (data && onNavigateToVersion) {
                onNavigateToVersion(data.document.id, data.version.version);
              }
              
              onUploadComplete?.();
            }, 500);
          } else if (status.status === "failed") {
            if (uploadPollIntervalRef.current) {
              clearInterval(uploadPollIntervalRef.current);
              uploadPollIntervalRef.current = null;
            }
            console.error("Processing failed");
            setIsUploading(false);
            setUploadProgress(0);
            setUploadStatusText("");
          }
        } catch (error) {
          // Log error but keep polling - transient network issues shouldn't kill the upload
          // The timeout will handle permanent failures
          console.error("Failed to poll status (will retry):", error);
        }
      }, 1000); // Poll every second
      
    } catch (error) {
      console.error("Upload failed:", error);
      if (uploadPollIntervalRef.current) {
        clearInterval(uploadPollIntervalRef.current);
        uploadPollIntervalRef.current = null;
      }
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatusText("");
    }
  };

  const handleFileSelect = () => {
    if (existingDocumentId && documentData) {
      setShowUploadDialog(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  // Get the latest version - fallback to first version if currentVersion isn't set
  const currentVersionNumber = documentData?.currentVersion;
  const latestVersion = currentVersionNumber 
    ? documentData?.versions?.find((v: any) => v.version === currentVersionNumber)
    : documentData?.versions?.[0];
  const currentPages = latestVersion?.pages || [];

  return (
    <>
      {existingDocumentId && documentData && latestVersion && showUploadDialog && (
        <PageUploadDialog
          open={showUploadDialog}
          onClose={() => {
            setShowUploadDialog(false);
            setUploadedPageData(null);
          }}
          documentId={existingDocumentId}
          versionId={latestVersion.id}
          versionNumber={latestVersion.version}
          currentPages={currentPages}
          documentColor={documentColor}
          preloadedData={uploadedPageData}
          entryMode="addPages"
          onPagesInserted={onPagesInserted}
          onProcessingComplete={onProcessingComplete}
          documentCurrentVersion={documentData.currentVersion}
        />
      )}
    
    <Card className="mb-0 border-0 shadow-none rounded-lg overflow-hidden">
      <div className="px-3 py-2 sm:px-6 sm:py-4 border-b border-border">
        <h2 className="text-base sm:text-xl font-semibold flex items-center">
          <Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-2" style={{ color: documentColor }} />
          {existingDocumentId ? "Upload New Version" : "Upload PDF Document"}
        </h2>
      </div>

      <CardContent className="p-3 sm:p-6">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file);
          }}
          className="hidden"
        />
        <input
          ref={addPagesFileInputRef}
          type="file"
          accept=".pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleAddPagesFileUpload(file);
          }}
          className="hidden"
        />

        <div className="grid gap-4">
          {/* Upload New Version Dropzone */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !isUploading) {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragEnter={(e) => handleUploadVersionDrag(e, true)}
            onDragOver={(e) => handleUploadVersionDrag(e, true)}
            onDragLeave={(e) => handleUploadVersionDrag(e, false)}
            onDrop={handleUploadVersionDrop}
            className={`border-2 border-dashed rounded-lg p-4 sm:p-8 text-center transition-all ${
              isUploading ? 'cursor-not-allowed' : 'cursor-pointer hover:border-opacity-100'
            } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
            style={{ 
              borderColor: isUploading ? documentColor : (uploadVersionDragActive ? documentColor : '#d1d5db'),
              backgroundColor: isUploading ? `${documentColor}10` : (uploadVersionDragActive ? `${documentColor}15` : 'transparent'),
              outline: (uploadVersionDragActive || isUploading) ? `2px solid ${documentColor}` : 'none',
              outlineOffset: (uploadVersionDragActive || isUploading) ? '-2px' : '0',
            }}
            aria-label="Upload new version dropzone"
            aria-describedby="upload-version-help"
            data-testid="dropzone-upload-new-version"
          >
            {isUploading ? (
              <>
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${documentColor}20` }}>
                  <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" style={{ color: documentColor }} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">
                  {uploadProgress < 20 ? "Uploading PDF..." : "Processing PDF..."}
                </h3>
                <p className="text-muted-foreground text-xs sm:text-sm mb-2">
                  {uploadStatusText || "Preparing..."}
                </p>
                {uploadProgress > 0 && (
                  <>
                    <div className="text-xs sm:text-sm font-medium mb-2" style={{ color: documentColor }}>
                      {uploadProgress}%
                    </div>
                    <Progress 
                      value={uploadProgress} 
                      className="h-2 max-w-xs mx-auto"
                      style={{ backgroundColor: `${documentColor}20` }}
                      indicatorStyle={{ backgroundColor: documentColor }}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${documentColor}20` }}>
                  <FileText className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: documentColor }} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">Upload New Version</h3>
                <p className="text-muted-foreground text-xs sm:text-sm mb-1">
                  Drop PDF here or click to browse
                </p>
                <p id="upload-version-help" className="text-[10px] sm:text-xs text-muted-foreground">
                  Create a complete new version with all pages from the PDF
                </p>
              </>
            )}
          </div>

          {/* Add Pages Dropzone */}
          {existingDocumentId && (
            <div
              role="button"
              tabIndex={0}
              onClick={() => !isAddPagesUploading && addPagesFileInputRef.current?.click()}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isAddPagesUploading) {
                  e.preventDefault();
                  addPagesFileInputRef.current?.click();
                }
              }}
              onDragEnter={(e) => handleAddPagesDrag(e, true)}
              onDragOver={(e) => handleAddPagesDrag(e, true)}
              onDragLeave={(e) => handleAddPagesDrag(e, false)}
              onDrop={handleAddPagesDrop}
              className={`border-2 border-dashed rounded-lg p-4 sm:p-8 text-center transition-all ${
                isAddPagesUploading ? 'cursor-not-allowed' : 'cursor-pointer hover:border-opacity-100'
              } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
              style={{ 
                borderColor: isAddPagesUploading ? documentColor : (addPagesDragActive ? documentColor : '#d1d5db'),
                backgroundColor: isAddPagesUploading ? `${documentColor}10` : (addPagesDragActive ? `${documentColor}15` : 'transparent'),
                outline: (addPagesDragActive || isAddPagesUploading) ? `2px solid ${documentColor}` : 'none',
                outlineOffset: (addPagesDragActive || isAddPagesUploading) ? '-2px' : '0',
              }}
              aria-label="Add pages dropzone"
              aria-describedby="add-pages-help"
              data-testid="dropzone-add-pages"
            >
              {isAddPagesUploading ? (
                <>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${documentColor}20` }}>
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" style={{ color: documentColor }} />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">Processing PDF...</h3>
                  <p className="text-muted-foreground text-xs sm:text-sm mb-2">
                    {addPagesStatusText || "Generating page previews"}
                  </p>
                  {addPagesProgress > 0 && (
                    <>
                      <div className="text-xs sm:text-sm font-medium mb-2" style={{ color: documentColor }}>
                        {addPagesProgress}%
                      </div>
                      <Progress 
                        value={addPagesProgress} 
                        className="h-2 max-w-xs mx-auto"
                        style={{ backgroundColor: `${documentColor}20` }}
                        indicatorStyle={{ backgroundColor: documentColor }}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${documentColor}20` }}>
                    <Upload className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: documentColor }} />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">Add Pages</h3>
                  <p className="text-muted-foreground text-xs sm:text-sm mb-1">
                    Drop PDF here or click to browse
                  </p>
                  <p id="add-pages-help" className="text-[10px] sm:text-xs text-muted-foreground">
                    Select specific pages from a PDF to insert into the current version
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </>
  );
}
